import React, { Suspense } from 'react';
import { ApolloProvider } from '@apollo/client';
import { Button, Modal, NativeBaseProvider, Text, VStack } from 'native-base';
import { RecoilRoot, useRecoilValue } from 'recoil';
import WebApp from './WebApp/WebApp';
import { theme } from './WebApp/Common/theme/ThemeProvider';
import { loadEwayECrypt } from './WebApp/Common/utils/eway';
import { isDoctorPortal } from './WebApp/Common/utils/isDoctorPortal';
import { LogBox } from 'react-native';
import { initScripts } from './WebApp/Common/utils/tracking/initScripts';
import { initCss } from './WebApp/Common/theme/initCss';
import { AuthProvider } from './WebApp/Common/providers/AuthProvider';
import { RouterProvider } from './WebApp/Common/providers/RouterProvider';
import { authenticationStateAtom } from './WebApp/Common/state/authenticationState';
import { useGraphQLClient } from './WebApp/Common/hooks/useGraphQLClient';
import { useAuthState } from './WebApp/Common/hooks/useAuthState';
import { useLogOut } from './WebApp/Common/hooks/useLogOut';
import Loading from './WebApp/Common/components/Loading';
import { withLDWrapper } from './WebApp/withLDWrapper';
import { useFonts } from './WebApp/Common/hooks/useFonts';
import { useBraze } from './WebApp/Common/hooks/useBraze';
import { useReferralCode } from './WebApp/Common/hooks/useReferralCode';
import { useReferralAttribution } from './WebApp/Common/hooks/useReferralAttribution';
import { useRouting } from './WebApp/Common/hooks/useRouting';
import { PushNotificationsProvider } from './WebApp/Common/providers/PushNotificationsProvider';
import { FaceIDProvider } from './WebApp/Common/providers/FaceIDProvider';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import './WebApp/Common/localise/i18n';
import { ToastProvider } from './WebApp/Common/providers/ToastProvider';
import { ConfigProvider } from './WebApp/Common/providers/ConfigProvider';
import './WebApp/Common/constants/mixpanel';
import * as braze from '@braze/web-sdk';

loadDevMessages();
loadErrorMessages();

if (__DEV__) {
  LogBox.ignoreLogs(['Please pass alt prop to Image component']); // Ignore log notification by message
  LogBox.ignoreAllLogs(true); //Ignore all log notifications

  const originalConsoleError = console.error;

  console.warn = (message, ...args) => {
    if (
      typeof message === 'string' &&
      (message.includes('Please pass alt prop to Image component') ||
        message.includes(
          'If you do not provide children, you must specify an aria-label for accessibility',
        ))
    ) {
      return;
    }
    originalConsoleError.apply(console, [message, ...args]);
  };
}

const DoctorVoiceProvider = React.lazy(
  () => import('./WebApp/DoctorVoice/components/DoctorVoiceProvider'),
);

const DoctorNotificationsProvider = React.lazy(
  () => import('./WebApp/Common/providers/NotificationsProvider'),
);

loadEwayECrypt();
initScripts();
initCss();

function AppWeb() {
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const metaTag = document.createElement('meta');
      metaTag.name = 'robots';
      metaTag.content = 'noindex, nofollow';
      document.head.appendChild(metaTag);
    }
  }, []);

  const { isLoading: isAuthLoading } = useRecoilValue(authenticationStateAtom);
  const { isLoading: isFontsLoading } = useFonts();

  if (isAuthLoading || isFontsLoading) return <Loading />;

  return (
    <>
      <WebApp />
    </>
  );
}

function AppDoctorPortal() {
  const {
    isExpired,
    isLoading: isAuthLoading,
  } = useRecoilValue(authenticationStateAtom);
  const { logOut } = useLogOut();

  const { pathname } = useRouting();

  if (isAuthLoading) {
    return (
      <NativeBaseProvider theme={theme}>
        <Loading />
      </NativeBaseProvider>
    );
  }

  return (
    <ToastProvider>
      <DoctorVoiceProvider>
        <DoctorNotificationsProvider>
          <ConfigProvider>
            <NativeBaseProvider theme={theme}>
              <WebApp />
              {isExpired && pathname !== '/signin' && (
                <Modal isOpen={true} size="xl">
                  <Modal.Content maxWidth="350">
                    <Modal.Header>Session Expired</Modal.Header>
                    <Modal.Body>
                      <VStack space={2}>
                        <Text>Your session has expired and you will need to re-login again.</Text>

                        <Text>Also ensure you are connected via CloudFlare WARP</Text>
                      </VStack>
                    </Modal.Body>
                    <Modal.Footer>
                      <Button flex="1" onPress={logOut}>
                        Login Again
                      </Button>
                    </Modal.Footer>
                  </Modal.Content>
                </Modal>
              )}
            </NativeBaseProvider>
          </ConfigProvider>
        </DoctorNotificationsProvider>
      </DoctorVoiceProvider>
    </ToastProvider>
  );
}

const RecoilWrapper = () => {
  return (
    <RouterProvider>
      <RecoilRoot>
        <AppWrapper />
      </RecoilRoot>
    </RouterProvider>
  );
};

const AppWrapper = function () {
  const graphClient = useGraphQLClient();

  return (
    <Suspense fallback={<Loading />}>
      <ApolloProvider client={graphClient}>
        <AuthProvider>
          <PushNotificationsProvider>
            <ConfigProvider>
              <AppOuter />
            </ConfigProvider>
          </PushNotificationsProvider>
        </AuthProvider>
      </ApolloProvider>
    </Suspense>
  );
};

const AppOuter = function () {
  useAuthState();
  useBraze();
  useReferralCode();
  useReferralAttribution();

  if (isDoctorPortal()) {
    return <AppDoctorPortal />;
  } else {
    braze.openSession();
  }

  return (
    <NativeBaseProvider theme={theme}>
      <FaceIDProvider>
        <AppWeb />
      </FaceIDProvider>
    </NativeBaseProvider>
  );
};

export default withLDWrapper(<RecoilWrapper />);
